import type { CustomChartDataResponse, CustomChartTableColumn, CustomChartTableRow } from '@/entities/dashboards';
import { groupedTableColumns, groupedTableRows, tableColorableCode } from '../DashboardPresetGrid/views/chartTable';

/**
 * Что в диаграмме окрашивается поимённо и какие колонки таблицы можно окрашивать: и то и другое
 * знает только последнее наполнение диаграммы, правило заранее этого не говорит.
 */

/** Таблица в глазах раскраски: её колонки и строки, а группа — ещё одна колонка строки. */
type ColoredTable = { columns: CustomChartTableColumn[]; rows: CustomChartTableRow[] };

/** Таблица наполнения, у таблицы с группировкой — с группой отдельной колонкой каждой строки. */
const coloredTable = (data: CustomChartDataResponse | undefined): ColoredTable | null => {
  if (data?.chartType === 'table') return { columns: data.payload.columns, rows: data.payload.rows };
  if (data?.chartType !== 'grouped-table') return null;
  return {
    // Название колонке группы даёт место показа: здесь нужны только её код и тип
    columns: groupedTableColumns(data.payload.columns, data.columns, ''),
    rows: data.payload.groups.flatMap((group) => groupedTableRows(group)),
  };
};

/**
 * Колонки таблицы, которые можно выбрать окрашиваемыми, — коды в порядке колонок последнего
 * наполнения. Развёрнутые колонки идут одним кодом: их состав меняется с данными, а выбор должен
 * пережить смену периода.
 */
export const chartColorableColumns = (data: CustomChartDataResponse | undefined): CustomChartTableColumn[] => {
  const table = coloredTable(data);
  if (!table) return [];
  const columns: CustomChartTableColumn[] = [];
  for (const column of table.columns) {
    const code = tableColorableCode(column);
    if (columns.some((known) => known.code === code)) continue;
    columns.push({ ...column, code });
  }
  return columns;
};

/**
 * Категории, которым задаётся цвет поимённо, в порядке их первого появления в последнем
 * наполнении.
 *
 * У столбчатой, круговой диаграмм и показателя с переходом к выборке это названия элементов. У
 * таблицы — текстовые значения окрашиваемых колонок: среди всех текстов таблицы оказались бы и
 * имена сотрудников, а окрасить нужно обычно одну колонку статуса или результата.
 */
export const chartColorCategories = (data: CustomChartDataResponse | undefined, coloredColumns: string[]): string[] => {
  if (data?.chartType === 'bar' || data?.chartType === 'pie') return unique(data.payload.points.map((p) => p.label));
  if (data?.chartType === 'selection-breakdown') return unique(data.payload.items.map((item) => item.label));

  const table = coloredTable(data);
  if (!table) return [];
  const textColumns = table.columns.filter(
    (column) => column.type === 'text' && coloredColumns.includes(tableColorableCode(column)),
  );
  return unique(
    table.rows.flatMap((row) =>
      textColumns
        .map((column) => row[column.code])
        .filter((value): value is string | number => value !== null && value !== undefined && value !== '')
        .map(String),
    ),
  );
};

/** Значения без повторов в порядке первого появления. */
const unique = (values: string[]): string[] => [...new Set(values)];
